(function () {
  'use strict';

  // Sentinel <option value> for "my model isn't listed", appended after a
  // brand's real models — see _onBrandChange/_onFind. Distinguishable from
  // any real index (those are always non-negative integers as strings).
  var NOT_LISTED_VALUE = '__not_listed__';

  // Maps a board's specific fin_box_system (e.g. "irocker_quick_lock") to
  // the family of fin we actually stock and sell for it (e.g.
  // "quick_lock") — see data.recommendable_families in the database and
  // buildSystemToFamily() below. A system with no family entry falls
  // through to the "no product available yet" state — see _matchKey.

  function el(tag, cls) {
    var node = document.createElement(tag);
    if (cls) node.className = cls;
    return node;
  }

  // Flattens { family: [system, system, ...] } into { system: family } for
  // O(1) lookup. Shared shape with assets/brand-fin-guide.js — both read
  // the same data.recommendable_families key so the finder result and the
  // brand guide's "fit" column can never drift out of sync again.
  function buildSystemToFamily(recommendableFamilies) {
    var map = {};
    Object.keys(recommendableFamilies || {}).forEach(function (family) {
      (recommendableFamilies[family] || []).forEach(function (system) {
        map[system] = family;
      });
    });
    return map;
  }

  function fillTemplate(str, tokens) {
    if (!str) return '';
    var out = str;
    Object.keys(tokens).forEach(function (key) {
      out = out.split(key).join(tokens[key]);
    });
    return out;
  }

  // Matches the slugs used in ?model= deep links: lowercase, diacritics
  // stripped, anything that isn't a-z/0-9 collapsed to a single hyphen.
  function slugify(str) {
    return (str || '')
      .toLowerCase()
      .normalize('NFKD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  function SupFinFinder(root) {
    this.root = root;
    this.databaseUrl = root.dataset.databaseUrl || '';

    this.strings = {};
    try {
      this.strings = JSON.parse(root.dataset.uiStrings || '{}');
    } catch (e) {}

    this.brandSelect = root.querySelector('[data-brand-select]');
    this.modelSelect = root.querySelector('[data-model-select]');
    this.findBtn = root.querySelector('[data-find-btn]');
    this.formError = root.querySelector('[data-form-error]');
    this.notSureLink = root.querySelector('[data-not-sure-link]');

    this.resultWrapper = root.querySelector('[data-result-wrapper]');
    this.resultBadge = root.querySelector('[data-result-badge]');
    this.resultHeading = root.querySelector('[data-result-heading]');
    this.resultSub = root.querySelector('[data-result-sub]');
    this.productsTarget = root.querySelector('[data-products-target]');
    this.productsSource = root.querySelector('[data-products-source]');
    this.noneBlock = root.querySelector('[data-none-block]');
    this.noneLearnLink = root.querySelector('[data-none-learn-link]');
    this.noneLearnLabel = root.querySelector('[data-none-learn-label]');

    this.brandPills = root.querySelector('[data-brand-pills]');

    this.step2 = root.querySelector('[data-step-2]');
    this.step3 = root.querySelector('[data-step-3]');
    this.step2DefaultText = this.step2 ? this.step2.textContent.trim() : '';

    this.feedbackWidget = root.querySelector('[data-feedback]');
    this.feedbackUpBtn = root.querySelector('[data-feedback-btn="up"]');
    this.feedbackDownBtn = root.querySelector('[data-feedback-btn="down"]');
    this.feedbackDetail = root.querySelector('[data-feedback-detail]');
    this.feedbackText = root.querySelector('[data-feedback-text]');
    this.feedbackEmail = root.querySelector('[data-feedback-email]');
    this.feedbackSubmitBtn = root.querySelector('[data-feedback-submit]');
    this.feedbackThanks = root.querySelector('[data-feedback-thanks]');
    this.feedbackForm = root.querySelector('[data-feedback-form-wrapper] form');
    this.feedbackFormBody = root.querySelector('[data-feedback-form-body]');
    this.feedbackFormEmail = root.querySelector('[data-feedback-form-email]');

    this.data = null;
    this.systemToFamily = {};
    this.currentContext = null;
    this.currentRating = null;

    if (this.brandSelect) this.brandSelect.disabled = true;
    if (this.modelSelect) this.modelSelect.disabled = true;

    this._bindEvents();
    this._loadDatabase();
  }

  SupFinFinder.prototype._bindEvents = function () {
    var self = this;

    if (this.brandSelect) {
      this.brandSelect.addEventListener('change', function () {
        self._onBrandChange();
      });
    }
    if (this.modelSelect) {
      this.modelSelect.addEventListener('change', function () {
        self._clearError();
        self._hideResult();
        self._resetSteps();
      });
    }
    if (this.findBtn) {
      this.findBtn.addEventListener('click', function () {
        self._onFind();
      });
    }

    if (this.feedbackUpBtn) {
      this.feedbackUpBtn.addEventListener('click', function () {
        self._onFeedbackRating('up');
      });
    }
    if (this.feedbackDownBtn) {
      this.feedbackDownBtn.addEventListener('click', function () {
        self._onFeedbackRating('down');
      });
    }
    if (this.feedbackSubmitBtn) {
      this.feedbackSubmitBtn.addEventListener('click', function () {
        self._onFeedbackSubmit();
      });
    }

    // Quick system pills + box-guide card CTAs — server-rendered, so these
    // can bind immediately without waiting on _loadDatabase. Each trigger
    // carries its own data-system-label/data-guide-url, so no lookup table
    // is needed here (see _onSystemSelect).
    this.root.querySelectorAll('[data-system-select]').forEach(function (trigger) {
      trigger.addEventListener('click', function () {
        self._onSystemSelect(trigger);
      });
    });
  };

  SupFinFinder.prototype._loadDatabase = function () {
    var self = this;
    if (!this.databaseUrl) return;

    fetch(this.databaseUrl)
      .then(function (res) {
        return res.json();
      })
      .then(function (json) {
        self.data = json;
        self.systemToFamily = buildSystemToFamily(json.recommendable_families);
        self._populateBrands();
        self._applyUrlPrefill();
      })
      .catch(function (err) {
        console.warn('[SupFinFinder] Could not load fin database', err);
      });
  };

  SupFinFinder.prototype._populateBrands = function () {
    var self = this;
    if (!this.data || !this.brandSelect) return;

    var brands = (this.data.brands || []).slice().sort(function (a, b) {
      return a.brand.localeCompare(b.brand);
    });

    // The select's only option at this point is the server-rendered
    // "Loading brands…" placeholder — swap its text now that loading is
    // done, otherwise the closed dropdown keeps showing that text forever
    // even once the real options below have been appended.
    var placeholder = this.brandSelect.querySelector('option[value=""]');
    if (placeholder) placeholder.textContent = this.strings.brandPlaceholder || 'Select a brand';

    brands.forEach(function (brand) {
      var opt = document.createElement('option');
      opt.value = brand.handle;
      opt.textContent = brand.brand;
      self.brandSelect.appendChild(opt);
    });
    this.brandSelect.disabled = false;

    if (this.brandPills) {
      var pillSubtitle = this.brandPills.dataset.brandPillSubtitle || '';
      brands.forEach(function (brand) {
        // A real href (not just a click handler) so the pill is
        // shareable/bookmarkable and works with cmd/ctrl-click — but a
        // plain click intercepts it for the smooth same-page experience.
        var pill = el('a', 'supfin__pill');
        pill.href = '?brand=' + encodeURIComponent(brand.handle);

        var brandName = el('span');
        brandName.textContent = brand.brand;
        pill.appendChild(brandName);

        if (pillSubtitle) {
          var subtitle = el('span', 'supfin__pill-subtitle');
          subtitle.textContent = pillSubtitle;
          pill.appendChild(subtitle);
        }

        pill.addEventListener('click', function (e) {
          e.preventDefault();
          self._selectBrand(brand.handle);
        });
        self.brandPills.appendChild(pill);
      });
    }
  };

  SupFinFinder.prototype._selectBrand = function (handle) {
    if (!this.brandSelect) return;
    this.brandSelect.value = handle;
    this._onBrandChange();
    this.root.scrollIntoView({ behavior: 'smooth', block: 'start' });
    this.brandSelect.focus();
  };

  // Deep-link support: ?brand=<brand.handle> pre-selects the brand (and
  // repopulates the model dropdown); ?model=<slugified model name>, on top
  // of that, also picks the model and runs the search — so a link like
  // /pages/sup-fin-finder?brand=red-paddle-co&model=ride-9-8-10-6-10-8
  // lands directly on the result, no re-entering what the link already
  // told us. Used by the brand pills below and, later, by brand/model
  // landing pages linking back into this same tool.
  SupFinFinder.prototype._applyUrlPrefill = function () {
    var params;
    try {
      params = new URLSearchParams(window.location.search);
    } catch (e) {
      params = null;
    }

    // ?brand= in the URL always wins (lets a link override); otherwise
    // fall back to the section's own default_brand setting, so a
    // dedicated brand landing page pre-selects itself with no query
    // string needed at all.
    var brandParam = (params && params.get('brand')) || this.root.dataset.defaultBrand || '';
    if (!brandParam || !this.brandSelect) return;

    var brand = this._findBrand(brandParam);
    if (!brand) return;

    this.brandSelect.value = brand.handle;
    this._onBrandChange();

    var modelParam = params && params.get('model');
    if (!modelParam || !this.modelSelect) return;

    var modelIndex = -1;
    brand.models.forEach(function (model, index) {
      if (modelIndex === -1 && slugify(model.model) === modelParam) modelIndex = index;
    });
    if (modelIndex === -1) return;

    this.modelSelect.value = String(modelIndex);
    this._onFind();
  };

  SupFinFinder.prototype._findBrand = function (handle) {
    if (!this.data) return null;
    var brands = this.data.brands || [];
    for (var i = 0; i < brands.length; i++) {
      if (brands[i].handle === handle) return brands[i];
    }
    return null;
  };

  SupFinFinder.prototype._onBrandChange = function () {
    var brand = this._findBrand(this.brandSelect.value);
    if (!this.modelSelect) return;

    this.modelSelect.innerHTML = '';
    var placeholder = document.createElement('option');
    placeholder.value = '';
    placeholder.textContent = brand
      ? this.strings.modelChoosePrompt || 'Choose your model'
      : this.strings.modelPickBrandPrompt || 'Pick a brand first';
    this.modelSelect.appendChild(placeholder);

    if (brand) {
      brand.models.forEach(function (model, index) {
        var opt = document.createElement('option');
        opt.value = String(index);
        opt.textContent = model.model;
        this.modelSelect.appendChild(opt);
      }, this);

      // Lets a shopper whose exact model is missing from the database still
      // get a (hedged, "likely") recommendation instead of dead-ending —
      // see _onFind for how this option is resolved.
      var notListedOpt = document.createElement('option');
      notListedOpt.value = NOT_LISTED_VALUE;
      notListedOpt.textContent = this.strings.modelNotListedOption || "My model isn't listed";
      this.modelSelect.appendChild(notListedOpt);

      this.modelSelect.disabled = false;
    } else {
      this.modelSelect.disabled = true;
    }

    this._clearError();
    this._hideResult();
    this._resetSteps();
  };

  // Updates the "1. SUP → 2. Your board → 3. Your fins" step trail once a
  // result is shown: step 2 gets checked off with the shopper's actual
  // choice (board name or bare system), step 3 goes from pending to active.
  // Mirrored by _resetSteps whenever the brand/model selection changes
  // again, so the trail doesn't keep claiming "done" for a stale choice.
  SupFinFinder.prototype._updateSteps = function (label) {
    if (this.step2 && label) {
      this.step2.textContent = '✓ ' + label;
      this.step2.classList.add('supfin__steps-done');
      this.step2.classList.remove('supfin__steps-active', 'supfin__steps-pending');
    }
    if (this.step3) {
      this.step3.classList.remove('supfin__steps-pending');
      this.step3.classList.add('supfin__steps-active');
    }
  };

  SupFinFinder.prototype._resetSteps = function () {
    if (this.step2) {
      this.step2.textContent = this.step2DefaultText;
      this.step2.classList.remove('supfin__steps-done');
      this.step2.classList.add('supfin__steps-active');
    }
    if (this.step3) {
      this.step3.classList.remove('supfin__steps-active');
      this.step3.classList.add('supfin__steps-pending');
    }
  };

  // Returns { system, state, resultKey } for a recommendable system — here
  // `system` is the FAMILY of fin we stock (e.g. "quick_lock"), not the
  // board's exact fin_box_system, since that's what result blocks are
  // keyed by (state is 'exact' when the mapping is confirmed, 'likely'
  // otherwise) — or null when we don't carry a matching fin yet.
  SupFinFinder.prototype._matchKey = function (brand, model) {
    if (!model || !model.fin_box_system) return null;
    var family = this.systemToFamily[model.fin_box_system];
    if (!family) return null;

    var confidence = model.confidence || (brand && brand.default_confidence);
    var state = confidence === 'confirmed' ? 'exact' : 'likely';

    return {
      system: family,
      state: state,
      resultKey: family + '__' + state,
    };
  };

  SupFinFinder.prototype._systemLabel = function (id) {
    var labels = this.strings.systemLabels || {};
    return labels[id] || (id || '').replace(/_/g, ' ');
  };

  SupFinFinder.prototype._showError = function (msg) {
    if (!this.formError) return;
    this.formError.textContent = msg;
    this.formError.hidden = false;
  };

  SupFinFinder.prototype._clearError = function () {
    if (!this.formError) return;
    this.formError.textContent = '';
    this.formError.hidden = true;
  };

  SupFinFinder.prototype._hideResult = function () {
    if (this.resultWrapper) this.resultWrapper.hidden = true;
  };

  SupFinFinder.prototype._onFind = function () {
    this._clearError();

    var brand = this._findBrand(this.brandSelect ? this.brandSelect.value : '');
    if (!brand) {
      this._showError(this.strings.errorBrand || 'Please choose your board brand first.');
      return;
    }

    var modelIndex = this.modelSelect ? this.modelSelect.value : '';
    if (modelIndex === '') {
      this._showError(this.strings.errorModel || 'Please choose your board model to continue.');
      return;
    }

    var model, match;
    if (modelIndex === NOT_LISTED_VALUE) {
      // Unconfirmed guess from the brand's own default system — always
      // "likely", never "exact", regardless of how confident we are about
      // this brand's *listed* models: we haven't actually seen this one.
      model = {
        model: this.strings.modelNotListedName || 'YOUR MODEL',
        fin_box_system: brand.default_fin_box_system || null,
      };
      var notListedFamily = model.fin_box_system ? this.systemToFamily[model.fin_box_system] : null;
      match = notListedFamily
        ? { system: notListedFamily, state: 'likely', resultKey: notListedFamily + '__likely' }
        : null;
    } else {
      model = brand.models[Number(modelIndex)];
      match = this._matchKey(brand, model);
    }
    var state = match ? match.state : 'none';

    this.currentContext = {
      brand: brand.brand,
      model: model.model,
      system: model.fin_box_system || null,
      state: state,
    };
    this._resetFeedback();
    this._trackEvent('result_shown', this.currentContext);

    var tokens = {
      '%%BRAND%%': brand.brand.toUpperCase(),
      '%%MODEL%%': (model.model || '').toUpperCase(),
      '%%SYSTEM%%': this._systemLabel(model.fin_box_system).toUpperCase(),
    };

    var guideUrls = this.strings.guideUrls || {};
    var guideUrl = model.fin_box_system ? guideUrls[model.fin_box_system] : null;

    this._displayResult({
      mode: 'board',
      state: state,
      tokens: tokens,
      resultKey: match ? match.resultKey : null,
      guideUrl: guideUrl,
      stepLabel: brand.brand + (model.model ? ' ' + model.model : ''),
    });
  };

  // Jump straight to a result for a bare fin-box family, skipping brand and
  // model entirely — used by the "already know your fin system?" quick
  // pills and the box-guide cards' "show compatible fins" CTA. `trigger` is
  // the clicked element; its data-system-select/-label/-guide-url are set
  // server-side per box_system block (see the box-guide loop in the
  // section), so no client-side lookup table is needed here.
  SupFinFinder.prototype._onSystemSelect = function (trigger) {
    var family = trigger.dataset.systemSelect || null;
    var label = trigger.dataset.systemLabel || '';
    var guideUrl = trigger.dataset.guideUrl || '';

    this._clearError();

    // A bare system (no family we stock a fin for) always resolves to the
    // "none" state — there's no "likely" without at least a brand's default
    // confidence to hedge on, and definitely no "exact" without a verified
    // model.
    var state = family ? 'likely' : 'none';
    var resultKey = family ? family + '__likely' : null;
    var tokens = { '%%SYSTEM%%': label.toUpperCase() };

    this.currentContext = { brand: null, model: null, system: family, state: state };
    this._resetFeedback();
    this._trackEvent('result_shown', this.currentContext);

    this._displayResult({
      mode: 'system',
      state: state,
      tokens: tokens,
      resultKey: resultKey,
      guideUrl: guideUrl,
      stepLabel: label,
    });
  };

  // Shared tail of _onFind/_onSystemSelect: fills in the result badge/
  // heading/sub, the "learn about this system" CTA, clones matching product
  // cards, and reveals the result. `mode: 'system'` hides the badge (the
  // mockup's system-only result card has none) and reads headingSystem/
  // subSystem instead of heading{State}/sub{State}, since there's no
  // %%BRAND%%/%%MODEL%% to fill into the board-mode copy.
  SupFinFinder.prototype._displayResult = function (opts) {
    var state = opts.state;
    var tokens = opts.tokens;
    var isSystemMode = opts.mode === 'system';

    if (this.resultBadge) {
      this.resultBadge.style.display = isSystemMode ? 'none' : '';
      if (!isSystemMode) {
        var badgeKey = 'badge' + state.charAt(0).toUpperCase() + state.slice(1);
        this.resultBadge.textContent = this.strings[badgeKey] || '';
      }
    }
    if (this.resultHeading) {
      var headingKey = isSystemMode
        ? state === 'none'
          ? 'headingSystemNone'
          : 'headingSystem'
        : 'heading' + state.charAt(0).toUpperCase() + state.slice(1);
      this.resultHeading.textContent = fillTemplate(this.strings[headingKey], tokens);
    }
    if (this.resultSub) {
      var subKey = isSystemMode
        ? state === 'none'
          ? 'subSystemNone'
          : 'subSystem'
        : 'sub' + state.charAt(0).toUpperCase() + state.slice(1);
      this.resultSub.textContent = fillTemplate(this.strings[subKey], tokens);
    }

    if (this.noneLearnLabel) {
      this.noneLearnLabel.textContent = fillTemplate(this.strings.learnSystemCta, tokens);
    }
    if (this.noneLearnLink && opts.guideUrl) {
      this.noneLearnLink.href = opts.guideUrl;
    }

    this._renderProducts(opts.resultKey);
    this._updateSteps(opts.stepLabel);

    if (this.resultWrapper) {
      this.resultWrapper.hidden = false;
      this.resultWrapper.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  SupFinFinder.prototype._renderProducts = function (resultKey) {
    if (this.productsTarget) this.productsTarget.innerHTML = '';

    var matches =
      resultKey && this.productsSource
        ? this.productsSource.querySelectorAll('[data-result-key="' + resultKey + '"]')
        : [];

    if (matches.length) {
      matches.forEach(
        function (node) {
          this.productsTarget.appendChild(node.cloneNode(true));
        }.bind(this)
      );
      if (this.productsTarget) this.productsTarget.hidden = false;
      if (this.noneBlock) this.noneBlock.hidden = true;
    } else {
      if (this.productsTarget) this.productsTarget.hidden = true;
      if (this.noneBlock) this.noneBlock.hidden = false;
    }
  };

  // Best-effort analytics: emits a DOM CustomEvent any listener (a Web
  // Pixel, GTM custom trigger, etc.) can hook into, and pushes to
  // gtag/dataLayer when the merchant already has them installed. Silently
  // no-ops otherwise — this never blocks the actual feedback delivery.
  SupFinFinder.prototype._trackEvent = function (name, detail) {
    var payload = Object.assign({ sectionId: this.root.id }, detail || {});

    try {
      document.dispatchEvent(
        new CustomEvent('sup-fin-finder:' + name, { bubbles: true, detail: payload })
      );
    } catch (e) {}

    try {
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'sup_fin_finder_' + name, payload);
      }
    } catch (e) {}

    try {
      if (window.dataLayer && typeof window.dataLayer.push === 'function') {
        window.dataLayer.push(Object.assign({ event: 'sup_fin_finder_' + name }, payload));
      }
    } catch (e) {}
  };

  SupFinFinder.prototype._resetFeedback = function () {
    this.currentRating = null;
    if (this.feedbackUpBtn) this.feedbackUpBtn.setAttribute('aria-pressed', 'false');
    if (this.feedbackDownBtn) this.feedbackDownBtn.setAttribute('aria-pressed', 'false');
    if (this.feedbackDetail) this.feedbackDetail.hidden = true;
    if (this.feedbackText) this.feedbackText.value = '';
    if (this.feedbackEmail) this.feedbackEmail.value = '';
    if (this.feedbackThanks) this.feedbackThanks.hidden = true;
    if (this.feedbackWidget) this.feedbackWidget.hidden = false;
  };

  SupFinFinder.prototype._onFeedbackRating = function (rating) {
    this.currentRating = rating;
    if (this.feedbackUpBtn)
      this.feedbackUpBtn.setAttribute('aria-pressed', String(rating === 'up'));
    if (this.feedbackDownBtn) {
      this.feedbackDownBtn.setAttribute('aria-pressed', String(rating === 'down'));
    }
    if (this.feedbackDetail) this.feedbackDetail.hidden = false;

    this._trackEvent(
      'feedback_rating',
      Object.assign({ rating: rating }, this.currentContext || {})
    );

    if (rating === 'down' && this.feedbackText) this.feedbackText.focus();
  };

  SupFinFinder.prototype._onFeedbackSubmit = function () {
    var comment = this.feedbackText ? this.feedbackText.value.trim() : '';
    var email = this.feedbackEmail ? this.feedbackEmail.value.trim() : '';

    this._trackEvent(
      'feedback_submit',
      Object.assign({ rating: this.currentRating, comment: comment }, this.currentContext || {})
    );

    this._deliverFeedback(comment, email);

    if (this.feedbackDetail) this.feedbackDetail.hidden = true;
    if (this.feedbackThanks) this.feedbackThanks.hidden = false;
  };

  // Forwards the free-text comment to the merchant via the theme's native
  // contact form (fetch + FormData), so feedback isn't just an analytics
  // event but something a human actually sees. Best effort — if it fails,
  // the analytics event above has already fired, so nothing is silently lost.
  SupFinFinder.prototype._deliverFeedback = function (comment, email) {
    if (!this.feedbackForm || (!comment && !email)) return;

    var context = this.currentContext || {};
    var lines = [
      'Rating: ' + (this.currentRating || 'n/a'),
      'Brand: ' + (context.brand || 'n/a'),
      'Model: ' + (context.model || 'n/a'),
      'Fin system: ' + (context.system || 'n/a'),
      'Result state: ' + (context.state || 'n/a'),
      'Page: ' + window.location.href,
      '',
      comment || '(no comment left)',
    ];

    if (this.feedbackFormBody) this.feedbackFormBody.value = lines.join('\n');
    if (this.feedbackFormEmail) this.feedbackFormEmail.value = email;

    var formData = new FormData(this.feedbackForm);
    fetch(this.feedbackForm.action, {
      method: 'POST',
      body: formData,
      headers: { Accept: 'application/json' },
    }).catch(function (err) {
      console.warn('[SupFinFinder] Could not deliver feedback comment', err);
    });
  };

  document.querySelectorAll('[data-sup-fin-finder-root]').forEach(function (root) {
    new SupFinFinder(root);
  });
})();
